import React, { createContext, useContext, useState } from 'react'

type CountrySelectionContextProviderProps = {
  children: React.ReactNode
}

export type CountrySelectionContext = {
  selectedCountry: string
  setSelectedCountry: React.Dispatch<React.SetStateAction<string>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  wwstCountryCodes: string[]
  regionNames: Intl.DisplayNames
}

export const CountrySelectionContext =
  createContext<CountrySelectionContext | null>(null)

export default function CountrySelectionContextProvider({
  children,
}: CountrySelectionContextProviderProps): JSX.Element {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
  const [selectedCountry, setSelectedCountry] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Ref for why these country codes are being used: https://smartthings.atlassian.net/browse/FEND-6994?focusedCommentId=1550938
  const wwstCountryCodes = [
    'AG',
    'AI',
    'AR',
    'AW',
    'BB',
    'BM',
    'BO',
    'BR',
    'BS',
    'BZ',
    'CA',
    'CL',
    'CO',
    'CR',
    'DM',
    'DO',
    'EC',
    'GD',
    'GF',
    'GT',
    'GY',
    'HN',
    'HT',
    'JM',
    'KN',
    'KY',
    'LC',
    'MS',
    'MX',
    'NI',
    'PA',
    'PE',
    'PR',
    'PY',
    'SR',
    'SV',
    'TC',
    'TT',
    'US',
    'UY',
    'VC',
    'VE',
    'VG',
    'AD',
    'AE',
    'AL',
    'AT',
    'BA',
    'BE',
    'BG',
    'CH',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FO',
    'FR',
    'GB',
    'GI',
    'GL',
    'GP',
    'GR',
    'HR',
    'HU',
    'IE',
    'IL',
    'IS',
    'IT',
    'LI',
    'LT',
    'LU',
    'LV',
    'MC',
    'ME',
    'MT',
    'NL',
    'NO',
    'PL',
    'PT',
    'QA',
    'RO',
    'RS',
    'SE',
    'SI',
    'SK',
    'SM',
    'TR',
    'VA',
    'ZA',
    'AU',
    'BD',
    'BN',
    'BT',
    'CK',
    'FJ',
    'FM',
    'HK',
    'ID',
    'IN',
    'JP',
    'KH',
    'KI',
    'KR',
    'LA',
    'LK',
    'MH',
    'MM',
    'MN',
    'MO',
    'MV',
    'MY',
    'NC',
    'NP',
    'NR',
    'NU',
    'NZ',
    'PF',
    'PG',
    'PH',
    'PW',
    'SB',
    'SG',
    'TH',
    'TL',
    'TO',
    'TV',
    'TW',
    'VN',
    'VU',
    'WS',
  ]

  return (
    <CountrySelectionContext.Provider
      value={{
        selectedCountry,
        setSelectedCountry,
        isLoading,
        setIsLoading,
        wwstCountryCodes,
        regionNames,
      }}
    >
      {children}
    </CountrySelectionContext.Provider>
  )
}

export function useCountrySelectionContext(): CountrySelectionContext {
  const context = useContext(CountrySelectionContext)
  if (!context) {
    throw new Error(
      'useCountrySelectionContext must be used within a CountrySelectionContextProvider'
    )
  }
  return context
}
